import { LandingLocale, LandingPublicLocale, resolveLandingLocale } from './copy';
import { LANDING_CATALOG_PAGES, getLandingCatalogPage, type LandingCatalogPageKey } from './siteContent';

export type LandingMarket = 'default' | 'es';
export type LandingPageKey =
  | 'platform'
  | 'emr-ehr'
  | 'patient-portal'
  | 'patient-app'
  | 'technology'
  | 'investors'
  | 'ai-emr'
  | 'triage-ai'
  | 'healthcare-analytics'
  | LandingCatalogPageKey;

export type SeoFaqItem = {
  question: string;
  answer: string;
};

export type LandingSeoData = {
  title: string;
  description: string;
  keywords: string;
  faq: SeoFaqItem[];
};

export type AeoAnswerItem = {
  question: string;
  answer: string;
};

type MarketInfo = {
  htmlLang: string;
  label: string;
  areaServedName: string;
};

const MARKET_INFO: Record<LandingMarket, MarketInfo> = {
  default: { htmlLang: 'en', label: 'Global', areaServedName: 'Global' },
  es: { htmlLang: 'es', label: 'Spanish', areaServedName: 'Spanish-speaking markets' },
};

const MARKET_PATH_PREFIXES: LandingMarket[] = ['es'];

const PAGE_PATHS: Partial<Record<LandingPageKey, string>> = {
  platform: '/',
  'emr-ehr': '/emr-ehr',
  'patient-portal': '/patient-portal',
  'patient-app': '/patient-app',
  technology: '/technology',
  investors: '/investors',
  'ai-emr': '/ai-emr',
  'triage-ai': '/triage-ai',
  'healthcare-analytics': '/healthcare-analytics',
  features: '/features',
  'clinical-ai': '/clinical-ai',
  'calendar-booking': '/calendar-booking',
  'billing-payments': '/billing-payments',
  'multi-site-operations': '/multi-site-operations',
  'reports-insights': '/reports-insights',
  'marketing-growth': '/marketing-growth',
  'who-we-serve': '/who-we-serve',
  'clinic-owners': '/clinic-owners',
  'practice-managers': '/practice-managers',
  practitioners: '/practitioners',
  'solo-practices': '/solo-practices',
  'growing-teams': '/growing-teams',
  'multi-location-groups': '/multi-location-groups',
  'primary-care-urgent-care': '/primary-care-urgent-care',
  'aesthetics-wellness': '/aesthetics-wellness',
  'mental-health-therapy': '/mental-health-therapy',
  'why-elen1': '/why-elen1',
  'case-studies': '/case-studies',
  security: '/security',
  comparisons: '/comparisons',
  resources: '/resources',
  'help-center': '/help-center',
  'developer-api': '/developer-api',
  guides: '/guides',
  pricing: '/pricing',
  'book-demo': '/book-demo',
};

const PAGE_LABEL_EN: Partial<Record<LandingPageKey, string>> = {
  platform: 'Clinic Software for Scheduling, Payments, and Team Operations',
  'emr-ehr': 'EMR / EHR with AI Workflow Automation',
  'patient-portal': 'Patient Portal for Appointments, Labs, and Care Plans',
  'patient-app': 'Patient Mobile App Experience',
  technology: 'Healthcare AI Features and Clinical Decision Intelligence',
  investors: 'Investors',
  'ai-emr': 'AI EMR Software for Clinical Workflows',
  'triage-ai': 'Clinical Triage AI and Queue Prioritization',
  'healthcare-analytics': 'Healthcare Analytics and Revenue Intelligence',
};

const PAGE_LABEL_ES: Partial<Record<LandingPageKey, string>> = {
  platform: 'Software clinico para agenda, pagos y operaciones del equipo',
  'emr-ehr': 'EMR / EHR con IA para Equipos Clinicos',
  'patient-portal': 'Portal del Paciente para Citas y Laboratorios',
  'patient-app': 'App de Paciente para Seguimiento de Cuidado',
  technology: 'Funciones de IA Clinica y Operativa',
  investors: 'Inversion en HealthTech con IA',
  'ai-emr': 'Software EMR con IA para flujo clinico',
  'triage-ai': 'IA de triaje clinico y priorizacion',
  'healthcare-analytics': 'Analitica clinica y de ingresos en salud',
};


const PAGE_DESCRIPTION_EN: Partial<Record<LandingPageKey, string>> = {
  platform: 'Elen1 brings scheduling, patient intake, reminders, payments, and team operations into one connected system for busy clinics.',
  'emr-ehr': 'EMR/EHR software with AI-supported charting, triage context, and connected workflows for clinics and multi-site care teams.',
  'patient-portal': 'Patient portal for appointments, lab results, care plans, and secure communication with clinical teams.',
  'patient-app': 'Mobile patient app for reminders, follow-ups, virtual care access, and continuous engagement between visits.',
  technology: 'Healthcare AI built into day-to-day clinical workflows, including triage support, chart context, and operational automation.',
  investors: 'Elen1 investor overview: a healthcare AI platform built around EMR, patient portal, telemedicine, scheduling, billing, and measurable execution outcomes.',
  'ai-emr': 'AI EMR software built for faster documentation, clearer patient context, and high-performance clinical operations.',
  'triage-ai': 'Clinical triage AI for patient prioritization, queue management, and safer response-time control in high-volume care settings.',
  'healthcare-analytics': 'Healthcare analytics for clinical and financial teams, including KPI dashboards, throughput monitoring, and revenue cycle visibility.',
};

const PAGE_DESCRIPTION_ES: Partial<Record<LandingPageKey, string>> = {
  platform: 'Elen1 integra agenda, admisión, recordatorios, pagos y operaciones del equipo en un solo sistema conectado para clínicas ocupadas.',
  'emr-ehr': 'EMR/EHR con IA para clinicas y hospitales en español, con expediente inteligente, flujos integrados y control operativo multi-sede.',
  'patient-portal': 'Portal del paciente en español con citas, resultados de laboratorio, planes de cuidado y mensajeria segura.',
  'patient-app': 'App de paciente en español con recordatorios, seguimiento clinico y acceso movil a la atencion de salud.',
  technology: 'Tecnologia de IA en salud en español, integrada en triage, contexto clinico y automatizacion del flujo diario.',
  investors: 'Panorama para inversionistas en healthtech con IA, orientado a escalabilidad operativa y adopcion clinica real en mercados hispanohablantes.',
  'ai-emr': 'Software EMR con IA en español, disenado para acelerar documentacion, decisiones clinicas y coordinacion asistencial.',
  'triage-ai': 'IA de triage en español con priorizacion automatica, mejor control de demanda y tiempos de respuesta clinica.',
  'healthcare-analytics': 'Analitica en salud en español con visibilidad de KPIs clinicos, operativos y financieros para direccion medica.',
};


const PAGE_KEYWORDS_EN: Partial<Record<LandingPageKey, string>> = {
  platform: 'AI EMR platform, scheduling, patient portal, telemedicine, billing, AI governance',
  'emr-ehr': 'EMR software, EHR software, AI charting, clinical workflow automation',
  'patient-portal': 'patient portal software, online appointment scheduling, lab results portal',
  'patient-app': 'patient app healthcare, mobile health app, telehealth follow-up',
  technology: 'healthcare AI, clinical decision support, triage automation',
  investors: 'healthtech investment, AI healthcare startup, EMR platform, patient portal, telemedicine, billing',
  'ai-emr': 'AI EMR software, intelligent medical records, clinical productivity',
  'triage-ai': 'triage AI, patient prioritization software, urgent care queue management',
  'healthcare-analytics': 'healthcare analytics platform, clinical KPI dashboard, revenue cycle analytics',
};

const PAGE_KEYWORDS_ES: Partial<Record<LandingPageKey, string>> = {
  platform: 'software medico en espanol, EMR con IA, EHR, historia clinica electronica, operacion clinica',
  'emr-ehr': 'EMR clinico en espanol, EHR clinico, expediente clinico digital, triaje con IA, software hospitalario',
  'patient-portal': 'portal del paciente, citas medicas online, resultados de laboratorio, experiencia paciente',
  'patient-app': 'app paciente, salud movil, telemedicina movil, seguimiento clinico, engagement paciente',
  technology: 'IA clinica, inteligencia artificial salud, soporte de decisiones medicas, automatizacion en salud',
  investors: 'inversion healthtech, startup salud, software medico con IA, SaaS salud',
  'ai-emr': 'EMR con IA, expediente clinico inteligente, software medico en espanol, productividad clinica',
  'triage-ai': 'triaje inteligente, priorizacion de pacientes, urgencias IA, gestion de demanda clinica',
  'healthcare-analytics': 'analitica hospitalaria, KPI clinicos, inteligencia de ingresos, BI salud',
};


const REGION_SEO_BLOCKS: Record<Exclude<LandingMarket, 'default'>, Partial<Record<LandingPageKey, { heading: string; body: string; bullets: string[] }>>> = {
  es: {
    platform: { heading: 'Plataforma clinica para mercados hispanohablantes', body: 'Elen1 integra atencion, operacion y seguimiento con IA para equipos de salud en español.', bullets: ['Flujos clinicos accionables.', 'Visibilidad operativa en tiempo real.', 'Experiencia unificada para paciente y equipo.'] },
    'emr-ehr': { heading: 'EMR / EHR en español para ejecucion real', body: 'Centraliza expediente clinico, laboratorio, mensajeria y facturacion en una sola capa operativa.', bullets: ['Contexto longitudinal del paciente.', 'Priorizacion de riesgos y pendientes.', 'Operacion multi-sede consistente.'] },
    'patient-portal': { heading: 'Portal del paciente en español', body: 'Autoservicio para citas, resultados, planes de cuidado y comunicacion segura con el equipo clinico.', bullets: ['Menos carga administrativa.', 'Mayor autonomia del paciente.', 'Continuidad asistencial entre consultas.'] },
    'patient-app': { heading: 'App movil para seguimiento continuo', body: 'Extiende el portal a movil para mantener adherencia y coordinacion diaria.', bullets: ['Recordatorios y alertas.', 'Telemedicina y mensajes seguros.', 'Acceso rapido a informacion clinica.'] },
    technology: { heading: 'Tecnologia de IA aplicada al flujo diario', body: 'La IA de Elen1 trabaja dentro de los procesos clinicos y operativos, no como modulo aislado.', bullets: ['Triage con priorizacion.', 'Resumen clinico accionable.', 'Soporte en tiempo real para equipos.'] },
    investors: { heading: 'Infraestructura healthtech para expansion regional', body: 'Propuesta de valor orientada a eficiencia operativa y crecimiento en mercados hispanos.', bullets: ['Modelo replicable por pais.', 'Diferenciacion clinica-tecnologica.', 'Adopcion basada en uso real.'] },
    'ai-emr': { heading: 'Software EMR con IA en español', body: 'Pensado para equipos clinicos que requieren rapidez, contexto y menos friccion documental.', bullets: ['Expediente con resumen inteligente.', 'Automatizacion de tareas repetitivas.', 'Mejor coordinacion asistencial.'] },
    'triage-ai': { heading: 'IA de triaje para priorizar mejor', body: 'Ordena la demanda clinica por riesgo para mejorar tiempos de respuesta y seguridad operativa.', bullets: ['Clasificacion por severidad.', 'Control de cuellos de botella.', 'Estandarizacion del triage.'] },
    'healthcare-analytics': { heading: 'Analitica en salud para decision ejecutiva', body: 'Conecta KPIs clinicos y financieros para direccion medica y operativa en una misma vista.', bullets: ['Paneles por sede y servicio.', 'Seguimiento de rendimiento.', 'Visibilidad del ciclo de ingresos.'] },
  },
};

export function getLandingMarketFromPath(pathname: string): LandingMarket {
  const normalized = pathname.toLowerCase();
  for (const market of MARKET_PATH_PREFIXES) {
    if (normalized === `/${market}` || normalized.startsWith(`/${market}/`)) return market;
  }
  return 'default';
}

export function stripMarketPrefix(pathname: string): string {
  const market = getLandingMarketFromPath(pathname);
  if (market === 'default') return pathname || '/';
  const stripped = pathname.slice(`/${market}`.length) || '/';
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

export function buildLandingPathForMarket(path: string, market: LandingMarket): string {
  const normalized = path === '' ? '/' : path.startsWith('/') ? path : `/${path}`;
  if (market === 'default') return normalized;
  if (normalized === '/') return `/${market}`;
  return `/${market}${normalized}`;
}

export function resolveLandingContext(pathname: string, language: string | undefined) {
  const market = getLandingMarketFromPath(pathname);
  const locale: LandingPublicLocale = market === 'default' ? resolveLandingLocale(language) : 'es';
  const marketInfo = MARKET_INFO[market];
  const htmlLang = market === 'default' ? locale : marketInfo.htmlLang;
  return { market, locale, htmlLang, marketInfo, basePath: stripMarketPrefix(pathname) };
}

export function getMarketInfo(market: LandingMarket) {
  return MARKET_INFO[market];
}

export function getPagePathByKey(pageKey: LandingPageKey): string {
  if (pageKey in LANDING_CATALOG_PAGES) {
    return LANDING_CATALOG_PAGES[pageKey as LandingCatalogPageKey].path;
  }
  return PAGE_PATHS[pageKey] ?? '/';
}

function joinHighlights(highlights: string[]) {
  if (highlights.length === 0) return 'connected clinic workflows';
  if (highlights.length === 1) return highlights[0].toLowerCase();
  if (highlights.length === 2) return `${highlights[0].toLowerCase()} and ${highlights[1].toLowerCase()}`;
  const allButLast = highlights.slice(0, -1).map((item) => item.toLowerCase()).join(', ');
  return `${allButLast}, and ${highlights[highlights.length - 1].toLowerCase()}`;
}

export function getSeoData(pageKey: LandingPageKey, market: LandingMarket, locale: LandingLocale = 'en'): LandingSeoData {
  const catalogPage = pageKey in LANDING_CATALOG_PAGES
    ? getLandingCatalogPage(pageKey as LandingCatalogPageKey, locale === 'es' ? 'es' : 'en')
    : null;
  if (catalogPage) {
    const title = market === 'default'
      ? `${catalogPage.title} | Elen1`
      : `${catalogPage.title} en ${MARKET_INFO[market].areaServedName} | Elen1`;
    const description = market === 'default'
      ? catalogPage.subtitle
      : `${catalogPage.subtitle} Regional fit for ${MARKET_INFO[market].areaServedName}.`;
    const keywords = `${catalogPage.title}, ${catalogPage.eyebrow}, clinic operations, Elen1`;
    const faq = [
      {
        question: 'What does this page cover?',
        answer: catalogPage.subtitle,
      },
      {
        question: 'What can teams improve with Elen1 here?',
        answer: `It focuses on improving ${joinHighlights(catalogPage.highlights)} inside a connected clinic operating system.`,
      },
      {
        question: 'Who is this most relevant for?',
        answer: 'It is most relevant for clinics evaluating this workflow, audience, or operating need as part of a more connected system.',
      },
      {
        question: 'How does it fit into the wider platform?',
        answer: 'Elen1 connects this area to scheduling, patient coordination, payments, reporting, and governance so teams are not operating in silos.',
      },
    ];
    return { title, description, keywords, faq: catalogPage.faq ?? faq };
  }
  if (market === 'default') {
    const label = PAGE_LABEL_EN[pageKey] ?? 'AI healthcare platform';
    const description = PAGE_DESCRIPTION_EN[pageKey] ?? 'Elen1 helps healthcare teams run scheduling, patient coordination, and clinical operations in one connected system.';
    const keywords = PAGE_KEYWORDS_EN[pageKey] ?? 'AI EMR platform, healthcare operations, clinic workflow software';
    const title =
      pageKey === 'platform'
        ? 'Elen1 — Clinic software that keeps your whole practice in sync'
        : pageKey === 'investors'
          ? 'Investors | Elen1'
          : `${label} | Elen1`;
    return {
      title,
      description,
      keywords: `${keywords}, healthcare AI`,
      faq: [
        { question: 'What does this page cover?', answer: description },
        { question: 'Who is this built for?', answer: 'Clinics, outpatient networks, and health systems that need connected workflows, better visibility, and more reliable execution.' },
        { question: 'How does Elen1 stand out here?', answer: 'The platform keeps AI, operations, and patient workflow in the same system instead of spreading them across separate tools.' },
        { question: 'Can it scale with a growing organization?', answer: 'Yes. Elen1 is designed to support lean clinics, growing teams, and multi-site organizations from the same operating foundation.' },
      ],
    };
  }

  const region = MARKET_INFO[market].areaServedName;
  const label = PAGE_LABEL_ES[pageKey] ?? 'Plataforma clinica con IA';
  const description = PAGE_DESCRIPTION_ES[pageKey] ?? 'Elen1 integra operacion clinica y coordinacion de pacientes para equipos de salud.';
  const keywords = PAGE_KEYWORDS_ES[pageKey] ?? 'software medico con IA, salud digital, operacion clinica';
  return {
    title: `${label} en ${region} | Elen1`,
    description: `${description} Enfoque regional para ${region}.`,
    keywords: `${keywords}, ${region}, salud digital`,
    faq: [
      { question: `Como aporta valor esta solucion en ${region}?`, answer: `Elen1 integra operacion clinica y coordinacion de pacientes para equipos de salud en ${region}.` },
      { question: 'Se adapta a equipos y sedes multiples?', answer: 'Si. La plataforma permite estandarizar procesos, medir desempeno y escalar operaciones multi-sede.' },
      { question: 'Incluye capacidades de IA en el flujo diario?', answer: 'Si. La IA se integra directamente en triage, expediente clinico y tareas operativas recurrentes.' },
      { question: 'Sirve para clinicas y hospitales hispanohablantes?', answer: 'Si. Existe una experiencia publica completa en espanol para clinicas, redes asistenciales y grupos multi-sede.' },
    ],
  };
}

export function getRegionalCopyBlock(pageKey: LandingPageKey, market: LandingMarket) {
  if (market === 'default') return null;
  const catalogPage = pageKey in LANDING_CATALOG_PAGES
    ? getLandingCatalogPage(pageKey as LandingCatalogPageKey, 'es')
    : null;
  if (catalogPage) {
    return {
      heading: `${catalogPage.title} para ${MARKET_INFO[market].areaServedName}`,
      body: `${catalogPage.subtitle} También muestra cómo el flujo puede adaptarse a equipos de salud regionales y grupos clínicos en crecimiento.`,
      bullets: catalogPage.highlights,
    };
  }
  return REGION_SEO_BLOCKS[market][pageKey];
}

export function getFooterCopy(market: LandingMarket, locale: LandingLocale = 'en') {
  if (market === 'default') {
    if (locale === 'es') {
      return {
        heading: 'Elen1',
        body: 'Software clínico conectado para equipos que necesitan operaciones más claras y seguimiento más confiable.',
        compliance: 'Diseñado para flujos de salud seguros, continuidad asistencial y responsabilidad diaria.',
        legal: 'Copyright 2026 Elen1 Systems. Todos los derechos reservados.',
      };
    }
    return {
      heading: 'Elen1',
      body: 'Connected clinic software for teams that need clearer operations and more reliable follow-through.',
      compliance: 'Built for secure healthcare workflows, continuity of care, and day-to-day accountability.',
      legal: 'Copyright 2026 Elen1 Systems. All rights reserved.',
    };
  }
  const region = MARKET_INFO[market].areaServedName;
  return {
    heading: `Elen1 ${region}`,
    body: `Plataforma clinica con IA para equipos de salud en ${region}.`,
    compliance: 'Disenada para continuidad asistencial, seguridad de datos y eficiencia operativa.',
    legal: `Copyright 2026 Elen1 Systems. ${region}.`,
  };
}

export function getAeoQuickAnswers(pageKey: LandingPageKey, market: LandingMarket, locale: LandingLocale = 'en'): AeoAnswerItem[] {
  const catalogPage = pageKey in LANDING_CATALOG_PAGES
    ? getLandingCatalogPage(pageKey as LandingCatalogPageKey, locale === 'es' ? 'es' : 'en')
    : null;
  if (catalogPage) {
    return [
      {
        question: locale === 'es' ? '¿En qué ayuda Elen1 aquí?' : 'What does Elen1 help with here?',
        answer: catalogPage.subtitle,
      },
      {
        question: locale === 'es' ? '¿Qué problemas ayuda a resolver?' : 'What problems does it help solve?',
        answer: locale === 'es' ? `Ayuda al equipo a mejorar ${joinHighlights(catalogPage.highlights)}.` : `It helps teams improve ${joinHighlights(catalogPage.highlights)}.`,
      },
      {
        question: locale === 'es' ? '¿Quién debería revisar esta página?' : 'Who should look at this page?',
        answer: locale === 'es' ? 'Equipos que evalúan este flujo, audiencia o necesidad operativa dentro de un sistema clínico más conectado.' : 'Teams evaluating this workflow, audience, or operating need inside a more connected clinic system should start here.',
      },
      {
        question: locale === 'es' ? '¿Cómo se conecta con el resto de Elen1?' : 'How does it connect to the rest of Elen1?',
        answer: locale === 'es' ? 'Se conecta con coordinación de pacientes, operaciones, pagos, reportes y gobierno operativo.' : 'It connects to surrounding workflows across patient coordination, operations, payments, reporting, and governance.',
      },
    ];
  }
  if (market === 'default') {
    const label = PAGE_LABEL_EN[pageKey] ?? 'this part of the platform';
    return [
      {
        question: 'What does this page cover?',
        answer: PAGE_DESCRIPTION_EN[pageKey] ?? 'It covers how Elen1 brings clinical workflows, patient coordination, and operational visibility into one system.',
      },
      {
        question: 'Who is this built for?',
        answer: 'Care teams, clinics, and healthcare groups that need one system for daily work and clearer visibility as they grow.',
      },
      {
        question: 'How is Elen1 different here?',
        answer: `Elen1 keeps ${label.toLowerCase()} connected to the rest of the operating workflow instead of treating it like a standalone tool.`,
      },
      {
        question: 'Can it scale with the organization?',
        answer: 'Yes. Elen1 is built for clinics and larger care organizations that need a system they can keep using as operations get more complex.',
      },
    ];
  }
  const region = MARKET_INFO[market].areaServedName;
  return [
    {
      question: `Que es ${PAGE_LABEL_ES[pageKey]} en ${region}?`,
      answer: `Es una solucion de salud digital para ${region} que integra atencion clinica, experiencia de paciente y gestion operativa.`,
    },
    {
      question: 'Para quien esta pensado?',
      answer: 'Para clinicas y redes asistenciales que requieren mas velocidad, control y continuidad de cuidado.',
    },
    {
      question: 'Que lo hace diferente?',
      answer: 'La IA esta integrada directamente en el flujo clinico y operativo diario, con foco en accion real.',
    },
    {
      question: 'Funciona para equipos de salud hispanohablantes?',
      answer: 'Si. La plataforma soporta adopcion para equipos de salud con una experiencia publica completa en espanol.',
    },
  ];
}
